import { useCallback, useRef, useState } from 'react';

const useScrollAnimation = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);
  const fallbackRef = useRef(null);

  const threshold = options.threshold ?? 0.1;
  const rootMargin = options.rootMargin ?? '0px 0px -50px 0px';
  const triggerOnce = options.triggerOnce ?? true;

  // A callback ref (rather than useEffect + useRef) so the observer attaches
  // whenever the DOM node actually appears — including when the element only
  // renders after data loads (e.g. a component that returns null until an
  // async fetch resolves). A plain useEffect only runs once on mount and
  // would silently skip setup if the node wasn't there yet on that first pass.
  const ref = useCallback((element) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (fallbackRef.current) {
      clearTimeout(fallbackRef.current);
      fallbackRef.current = null;
    }
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) observer.unobserve(element);
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(element);
    observerRef.current = observer;

    // Safety net: a fast or instant scroll (mouse-wheel flick, Page Down,
    // or a programmatic/restored scroll position) can jump an element
    // straight past the observer's intersection window without ever
    // registering it as visible, leaving it stuck at opacity 0 forever.
    // Force it visible after a bounded delay so content is never lost.
    fallbackRef.current = setTimeout(() => setIsVisible(true), 2000);
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

export default useScrollAnimation;
